import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Role } from "util/config/roles";
import WorkflowKind, {
  isAbsenceKind,
  isProfileChangeKind
} from "util/config/workflowKind";
import WorkerStatistics from "../../entities/WorkerStatistics";
import WorkflowStatistics from "../../entities/WorkflowStatistics";
import i18n from "../../i18n";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import SliceSingletonBase from "../types/SliceSingletonBase";
import { authHeader, getWorkerId } from "../users/auth";

// RESPONSE DECLARATIONS //
interface WorkersStatisticsResponse {
  AvailableEmployeesCount: number;
  BinaryEmployeesCount: number;
  FemaleEmployeesCount: number;
  FilledPositionsCount: number;
  MaleEmployeesCount: number;
  OnVacationEmployeesCount: number;
  SickEmployeesCount: number;
  TotalEmployeesCount: number;
  VacantPositionsCount: number;
}

// SLICE //
interface State {
  workers: SliceSingletonBase<WorkerStatistics>;
  workflows: SliceSingletonBase<WorkflowStatistics>;
}

const initialState: State = {
  workers: {
    data: null,
    loading: false,
    lastFetch: null,
    error: null
  },
  workflows: {
    data: null,
    loading: false,
    lastFetch: null,
    error: null
  }
};

const slice = createSlice({
  name: "statistics",
  initialState,
  reducers: {
    // SUBORDINATE STATISTICS
    subordinatesStatisticsRequested: statistics => {
      statistics.workers.loading = true;
      statistics.workers.error = null;
    },
    subordinatesStatisticsReceived: (
      statistics,
      action: PayloadAction<WorkersStatisticsResponse>
    ) => {
      statistics.workers.data = workerStatisticsSchema(action.payload);
    },
    subordinatesStatisticsRequestFailed: (
      statistics,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      statistics.workers.loading = false;
      statistics.workers.error = action.payload;
    },

    // SUBORDINATE STATISTICS
    workflowsStatisticsRequested: statistics => {
      statistics.workflows.loading = true;
      statistics.workflows.error = null;
    },
    workflowsStatisticsReceived: (
      statistics,
      action: PayloadAction<{
        data: number;
        extraData: { role: Role; kinds: WorkflowKind[] };
      }>
    ) => {
      const { data, extraData } = action.payload;

      if (!extraData || !extraData.kinds || !extraData.role) {
        console.error("Invalid payload structure", action.payload);
        return;
      }

      const { role, kinds } = extraData;

      // Initialize workflows.data if it is null
      if (statistics.workflows.data === null) {
        statistics.workflows.data = {
          AbsenceWorkflowsCountForHumanResources: 0,
          AbsenceWorkflowsCountForManagers: 0,
          AbsenceWorkflowCountForAprovers: 0,
          ProfileWorkflowCountForAprovers: 0,
          ProfileWorkflowsCountForHumanResources: 0,
          ProfileWorkflowsCountForManagers: 0
        };
      }

      kinds.forEach(kind => {
        if (kind === WorkflowKind.Leave) {
          // Map Leave kind to Absence workflows
          if (role === Role.HumanResources) {
            statistics.workflows.data!.AbsenceWorkflowsCountForHumanResources! =
              data;
          } else if (role === Role.Manager) {
            statistics.workflows.data!.AbsenceWorkflowsCountForManagers! = data;
          }
        } else {
          // Map other kinds to Profile workflows
          if (role === Role.HumanResources) {
            statistics.workflows.data!.ProfileWorkflowsCountForHumanResources! =
              data;
          } else if (role === Role.Manager) {
            statistics.workflows.data!.ProfileWorkflowsCountForManagers! = data;
          }
        }
      });
    },
    workflowsStatisticsRequestFailed: (
      statistics,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      statistics.workflows.loading = false;
      statistics.workflows.error = action.payload;
    }
  }
});

const {
  subordinatesStatisticsRequested,
  subordinatesStatisticsReceived,
  subordinatesStatisticsRequestFailed,

  workflowsStatisticsRequested,
  workflowsStatisticsReceived,
  workflowsStatisticsRequestFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getSubordinatesStatistics =
  () => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.statistics.workers.loading) {
      return;
    }

    const workerId = getWorkerId(getState());
    const url = "/api/workers/subordinateStats/" + workerId;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: subordinatesStatisticsRequested.type,
        onSuccess: subordinatesStatisticsReceived.type,
        onError: subordinatesStatisticsRequestFailed.type
      })
    );
  };

//TODO: This backend method is not what is needed. It should return object with respective role and workflowkind counts, not "data: number".
// For as long as the backend is not updated this workaround is used.
export const getWorkflowsStatistics =
  (role: Role, kinds: WorkflowKind[]) =>
  async (dispatch: AppDispatch, getState: () => AppState) => {
    const hasProfileKinds = kinds.some(isProfileChangeKind);
    const hasAbsenceKinds = kinds.some(isAbsenceKind);

    if (hasProfileKinds && hasAbsenceKinds) {
      console.error("Leave and other WorkflowKinds can't be queried at once!");
      return;
    }

    const currentWorkerId = getWorkerId(getState());
    const queryParams = new URLSearchParams();

    if (currentWorkerId !== undefined) {
      queryParams.append("workerId", currentWorkerId.toString());
    } else return;
    const rolesArray = [role];
    rolesArray.forEach(role => queryParams.append("role", role));
    kinds.forEach(kind => queryParams.append("kind", kind.toString()));

    const url = `/api/Workflow/GetActiveWorkflowCountForApprover?${queryParams.toString()}`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: workflowsStatisticsRequested.type,
        onSuccess: workflowsStatisticsReceived.type,
        onError: workflowsStatisticsRequestFailed.type,
        extraData: { role, kinds }
      })
    );
  };

// NORMALIZATION //
const workerStatisticsSchema = (
  payload: WorkersStatisticsResponse
): WorkerStatistics | null => {
  if (!payload) {
    return null;
  }
  return {
    AvailableEmployeesCount: payload.AvailableEmployeesCount,
    BinaryEmployeesCount: payload.BinaryEmployeesCount,
    FemaleEmployeesCount: payload.FemaleEmployeesCount,
    FilledPositionsCount: payload.FilledPositionsCount,
    MaleEmployeesCount: payload.MaleEmployeesCount,
    OnVacationEmployeesCount: payload.OnVacationEmployeesCount,
    SickEmployeesCount: payload.SickEmployeesCount,
    TotalEmployeesCount: payload.TotalEmployeesCount,
    VacantPositionsCount: payload.VacantPositionsCount
  };
};
